import * as React from 'react';
import styles from './index.less';
import fReadLocalFiles from '@/components/fReadLocalFiles';
import fMessage from '@/components/fMessage';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import FLoading from '@/components/FIcons/FLoading';
import img_idCardBack from '@/assets/images/idCardBack.png';
import img_idCardFront from '@/assets/images/idCardFront.png';

interface FidPhotoUploadProps {
  onFrontSuccess?: (info: { certImagePath: string; name: string; idNumber: string }) => void;
  onBackSuccess?: (info: { certImagePath: string; validPeriod: string }) => void;
  /** 根节点额外 class，例如弹窗内居中收窄宽度 */
  className?: string;
}

interface FidPhotoUploadStates {
  frontState: 'normal' | 'uploading' | 'success';
  frontImage: string;
  backState: 'normal' | 'uploading' | 'success';
  backImage: string;
}

const initStates: FidPhotoUploadStates = {
  frontState: 'normal',
  frontImage: '',
  backState: 'normal',
  backImage: '',
};

const ID_PHOTO_ACCEPT = '.jpg,.jpeg,.png';
const ID_PHOTO_EXTS = new Set(['jpg', 'jpeg', 'png']);
const ID_PHOTO_FORMAT_ERROR = '图片格式仅支持JPEG、PNG';

function isAcceptedIdPhotoFile(file: File) {
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext) {
    return ID_PHOTO_EXTS.has(ext);
  }
  return file.type === 'image/jpeg' || file.type === 'image/png';
}

async function pickIdPhotoFile(sizeErrorMessage: string): Promise<File | null> {
  const files = await fReadLocalFiles({ accept: ID_PHOTO_ACCEPT });
  const file = files?.[0];
  if (!file) {
    return null;
  }
  if (!isAcceptedIdPhotoFile(file)) {
    fMessage(ID_PHOTO_FORMAT_ERROR, 'error');
    return null;
  }
  if (file.size > 1024 * 1024 * 2) {
    fMessage(sizeErrorMessage, 'error');
    return null;
  }
  return file;
}

function FidPhotoUpload({ onFrontSuccess, onBackSuccess, className }: FidPhotoUploadProps) {
  const [$frontState, set$frontState, get$frontState] = FUtil.Hook.useGetState<
    FidPhotoUploadStates['frontState']
  >(initStates['frontState']);
  const [$frontImage, set$frontImage, get$frontImage] = FUtil.Hook.useGetState<
    FidPhotoUploadStates['frontImage']
  >(initStates['frontImage']);
  const [$backState, set$backState, get$backState] = FUtil.Hook.useGetState<
    FidPhotoUploadStates['backState']
  >(initStates['backState']);
  const [$backImage, set$backImage, get$backImage] = FUtil.Hook.useGetState<
    FidPhotoUploadStates['backImage']
  >(initStates['backImage']);

  return (
    <div className={[styles.idPhotoContainer, className].filter(Boolean).join(' ')}>
      <div
        className={styles.photoUpload}
        onClick={async () => {
          const file = await pickIdPhotoFile('身份证人像面照片大小不能超过2MB');
          if (!file) {
            return;
          }

          const imageUrl = URL.createObjectURL(file);
          set$frontState('uploading');
          set$frontImage(imageUrl);

          const {
            ret,
            errCode,
            msg,
            data,
          }: {
            ret: number;
            errCode: number;
            msg: string;
            data: {
              certImagePath: string;
              idCardInfo: {
                data: {
                  face: {
                    data: {
                      address: string;
                      birthDate: string;
                      ethnicity: string;
                      idNumber: string;
                      name: string;
                      sex: string;
                    };
                  };
                };
              };
            };
          } = await FServiceAPI.Payment.uploadIDCard({
            file,
          });
          if (ret !== 0 || errCode !== 0) {
            set$frontState('normal');
            set$frontImage('');
            fMessage(msg, 'error');
            return;
          }

          if (!data.idCardInfo?.data?.face) {
            set$frontState('normal');
            set$frontImage('');
            return;
          }

          set$frontState('success');
          onFrontSuccess?.({
            certImagePath: data.certImagePath,
            name: data.idCardInfo?.data?.face?.data?.name ?? '',
            idNumber: data.idCardInfo?.data?.face?.data?.idNumber ?? '',
          });
        }}
      >
        {$frontState !== 'normal' ? (
          <div className={styles.photoUploadContainer}>
            <img
              src={$frontImage}
              alt={
                // 身份证人像面照片
                FI18n.i18nNext.tAuto('payout_setting_entity_input_idphoto_info')
              }
              style={{ width: '100%', height: '100%', display: 'block' }}
            />
            {$frontState === 'uploading' && (
              <div className={styles.photoUploadContainerMask}>
                <FLoading style={{ fontSize: 24, color: '#FFFFFF' }} />
              </div>
            )}
          </div>
        ) : (
          <div className={styles.photoPlaceholder}>
            <img
              src={img_idCardFront}
              alt={
                // 身份证正面照片
                FI18n.i18nNext.tAuto('payout_setting_entity_input_idphoto_info')
              }
              style={{ width: 105, height: 57, display: 'block' }}
            />
            <div style={{ height: 12 }} />
            <div className={styles.photoPlaceholderText}>
              {
                // 身份证正面照片
                FI18n.i18nNext.tAuto('payout_setting_entity_input_idphoto_info')
              }
            </div>
          </div>
        )}
      </div>
      <div
        className={styles.photoUpload}
        onClick={async () => {
          const file = await pickIdPhotoFile('身份证国徽面照片大小不能超过2MB');
          if (!file) {
            return;
          }

          const imageUrl = URL.createObjectURL(file);
          set$backState('uploading');
          set$backImage(imageUrl);

          const {
            ret,
            errCode,
            msg,
            data,
          }: {
            ret: number;
            errCode: number;
            msg: string;
            data: {
              certImagePath: string;
              idCardInfo: {
                data: {
                  back: {
                    data: {
                      issueAuthority: string;
                      validPeriod: string;
                    };
                  };
                };
              };
            };
          } = await FServiceAPI.Payment.uploadIDCard({
            file,
          });

          if (ret !== 0 || errCode !== 0) {
            set$backState('normal');
            set$backImage('');
            fMessage(msg, 'error');
            return;
          }

          if (!data.idCardInfo?.data?.back) {
            set$backState('normal');
            set$backImage('');
            return;
          }
          set$backState('success');
          onBackSuccess?.({
            certImagePath: data.certImagePath,
            validPeriod: data.idCardInfo?.data?.back?.data?.validPeriod ?? '',
          });
        }}
      >
        {$backState !== 'normal' ? (
          <div className={styles.photoUploadContainer}>
            <img
              src={$backImage}
              alt={
                // 身份证国徽面照片
                FI18n.i18nNext.tAuto('payout_setting_entity_input_idphoto_info2')
              }
              style={{ width: '100%', height: '100%', display: 'block' }}
            />
            {$backState === 'uploading' && (
              <div className={styles.photoUploadContainerMask}>
                <FLoading style={{ fontSize: 24, color: '#FFFFFF' }} />
              </div>
            )}
          </div>
        ) : (
          <div className={styles.photoPlaceholder}>
            <img
              src={img_idCardBack}
              alt={
                // 身份证国徽面照片
                FI18n.i18nNext.tAuto('payout_setting_entity_input_idphoto_info2')
              }
              style={{ width: 109, height: 55, display: 'block' }}
            />
            <div style={{ height: 12 }} />
            <div className={styles.photoPlaceholderText}>
              {
                // 身份证国徽面照片
                FI18n.i18nNext.tAuto('payout_setting_entity_input_idphoto_info2')
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FidPhotoUpload;
